/**
 * Social presence for the Marketing tab: a read view per platform, an edit form,
 * and a one-click "Verify" that stamps a platform as re-checked. Saves go to the
 * marketing endpoint, which stamps the platforms whose details changed.
 *
 * Differences from the design, kept on purpose:
 * - Followers is called "audience" in the UI because the API field is `audienceSize`.
 * - Cadence "Unknown" is sent as null; the API's UNKNOWN value is never written.
 * - There is no "set unknown" button: blank audience, or the Unknown cadence
 *   option, already means unknown.
 * - A bare domain in Website gets `https://` added before it is sent.
 * - Audience or cadence entered without a handle is an error on the handle
 *   field, not silently dropped.
 * - The usual "audit trail" and "rarely change" footer lines are left out.
 */
import type { VenueDetail } from '../../../../api/venues';

import { EditSection } from './EditSection';
import { SocialEditForm } from './SocialEditForm';
import { SocialPresenceView } from './SocialPresenceView';
import { useSocialEditor } from './use-social-editor';

interface SocialPresenceSectionProps {
  venue: VenueDetail;
  /** Whether the server lets this caller edit the venue's marketing. */
  canEdit: boolean;
  /** Stack the platform rows for a narrow screen. */
  compact: boolean;
}

export function SocialPresenceSection({ venue, canEdit, compact }: SocialPresenceSectionProps) {
  const editor = useSocialEditor(venue);
  return (
    <EditSection
      first
      title="Social presence"
      hint="per platform · powers co-marketing"
      readOnly={!canEdit}
      footerNote={null}
      onCancel={editor.cancel}
      onSave={editor.save}
      view={
        <SocialPresenceView
          social={venue.social}
          compact={compact}
          onVerify={canEdit ? editor.verify : undefined}
          busy={editor.busy}
          notice={editor.notice}
        />
      }
      form={
        <SocialEditForm
          draft={editor.draft}
          onChange={editor.change}
          errors={editor.errors}
          social={venue.social}
          compact={compact}
        />
      }
    />
  );
}
